/**
 * Electron desktop integration hook.
 *
 * When the app is running inside the VACARS5 Electron shell, `window.electronAPI`
 * exists. This hook:
 *   - pushes the current session (bearer token + api base) into the main
 *     process so main can POST telemetry to the API from Node,
 *   - subscribes to sim status + telemetry events emitted by main,
 *   - tells main which live_id is currently active so telemetry is scoped.
 *
 * Outside Electron the hook returns { desktop: false } and does nothing.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { getToken, isRemembered } from "@/lib/session-store";

const IS_DESKTOP = typeof window !== "undefined" && !!window.electronAPI;

export function useElectronSim(activeFlight) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ connected: false, sim: null });
  const [telemetry, setTelemetry] = useState(null);

  // Push auth session down to main process so it can POST telemetry.
  useEffect(() => {
    if (!IS_DESKTOP) return;
    const token = getToken();
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (token && apiBase) {
      // `remember`: main keeps an unremembered session in memory only.
      window.electronAPI.session.set({ token, apiBase, user, remember: isRemembered() });
    } else {
      window.electronAPI.session.clear();
    }
  }, [user]);

  // Tell main which flight is active so telemetry posting scopes correctly,
  // and what the overlay shows about it. Keyed on more than the id: the
  // server resolves gates and airport positions after the flight starts, and
  // keyed on the id alone the overlay never heard about either.
  const overlayKey = activeFlight
    ? [activeFlight.id, activeFlight.gateOrig, activeFlight.gateDest,
       activeFlight.orig_pos, activeFlight.dest_pos].map((v) => String(v ?? "")).join("|")
    : "";
  useEffect(() => {
    if (!IS_DESKTOP) return;
    window.electronAPI.acars.setActive(activeFlight || null);
  }, [overlayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to sim events.
  useEffect(() => {
    if (!IS_DESKTOP) return;
    let mounted = true;
    window.electronAPI.sim.status().then((s) => mounted && setStatus(s));
    const offS = window.electronAPI.sim.onStatus((s) => setStatus(s));
    const offT = window.electronAPI.sim.onTelemetry((t) => setTelemetry(t));
    return () => { mounted = false; offS?.(); offT?.(); };
  }, []);

  return { desktop: IS_DESKTOP, status, telemetry };
}

export const isDesktop = () => IS_DESKTOP;

/**
 * "0.9.0 beta" in the desktop app, null in a browser. Every build used to say
 * 1.0.0 and the app showed no version at all, so neither a pilot nor support
 * could tell which build was running. 0.x is the beta series (2026-10-03).
 */
export function useAppVersion() {
  const [version, setVersion] = useState(null);
  useEffect(() => {
    if (!IS_DESKTOP || !window.electronAPI.appVersion) return;
    let live = true;
    window.electronAPI.appVersion().then((v) => live && setVersion(v)).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!version) return null;
  return String(version).startsWith("0.") ? `${version} beta` : version;
}

/**
 * Whether position reports are reaching FlyVAC, as the desktop app sees it
 * (telemetry-notice.js says what to show). Null in a browser, or with a
 * desktop app too old to say. Re-renders every few seconds while delivery is
 * failing, so a notice appears once its grace period is up.
 */
export function useTelemetryStatus() {
  const [status, setStatus] = useState(null);
  const [badSince, setBadSince] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => {
    const api = IS_DESKTOP ? window.electronAPI.acars : null;
    if (!api?.onTelemetryStatus) return undefined;
    let live = true;
    const take = (s) => {
      if (!live) return;
      setStatus(s || null);
      const bad = s && !["ok", "filed"].includes(s.state);
      setBadSince((prev) => (bad ? prev ?? Date.now() : null));
    };
    api.telemetryStatus?.().then(take).catch(() => {});
    const off = api.onTelemetryStatus(take);
    return () => { live = false; off?.(); };
  }, []);
  useEffect(() => {
    if (badSince == null) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, [badSince]);
  return { status, badSince };
}
